/* hero-picture: art-layer reveal (logo / handwriting animation).
 * Progressive enhancement: CSS shows every `.hero-picture__art-layer`
 * plainly until `js-hero-art` is present on <html> (added synchronously
 * below, before anything else runs) — a visitor without JS, or who hits
 * an error before this file finishes, always sees the layers, never a
 * permanently-hidden one. Once the hero's own image has loaded AND
 * scrolled into view, `hero-picture__art-run` is added to `.banner__media`
 * and the per-layer fade/wipe keyframes (assets/section-hero-picture.css)
 * take over; prefers-reduced-motion is handled entirely in CSS (layers
 * just stay visible), no branching needed here for that. */
(() => {
  document.documentElement.classList.add('js-hero-art');

  // Feature-detect @property support (via its JS twin, CSS.registerProperty)
  // so wipe layers can fall back to a plain opacity fade — see the
  // `.hero-picture__no-wipe` rule in section-hero-picture.css — instead of
  // silently animating nothing on browsers that can't interpolate a
  // custom property.
  let supportsAnimatableCustomProps = false;
  try {
    if (window.CSS && typeof window.CSS.registerProperty === 'function') {
      window.CSS.registerProperty({
        name: '--hero-art-feature-test',
        syntax: '<percentage>',
        inherits: false,
        initialValue: '0%',
      });
      supportsAnimatableCustomProps = true;
    }
  } catch {
    supportsAnimatableCustomProps = false;
  }
  if (!supportsAnimatableCustomProps) {
    document.documentElement.classList.add('hero-picture__no-wipe');
  }

  const runLayers = (media) => {
    if (!media || media.classList.contains('hero-picture__art-run')) return;
    media.classList.add('hero-picture__art-run');
  };

  const armMedia = (media) => {
    const img = media.querySelector('picture > img');
    const start = () => runLayers(media);

    // Already loaded (cached image, or this ran after load) — and once
    // loaded, only reveal once the hero has actually scrolled into view
    // rather than immediately (it's usually in view already on first
    // load, but this also covers a hero further down the page).
    const whenLoaded = (cb) => {
      if (img && !img.complete) {
        img.addEventListener('load', cb, { once: true });
        img.addEventListener('error', cb, { once: true });
      } else {
        cb();
      }
    };

    whenLoaded(() => {
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                start();
                observer.disconnect();
              }
            });
          },
          { rootMargin: '0px', threshold: 0.01 }
        );
        observer.observe(media);
      } else {
        start();
      }
    });
  };

  const initArt = (root = document) => {
    root.querySelectorAll('.hero-picture .banner__media').forEach((media) => {
      if (media.querySelector('.hero-picture__art-layer')) armMedia(media);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initArt());
  } else {
    initArt();
  }
  document.addEventListener('shopify:section:load', (event) => initArt(event.target));
})();

/* hero-picture: "Single line" heading size.
 * Scales .hero-picture__heading--fit so the text fills the width of its
 * box on exactly one line. Re-runs on resize, font load and theme-editor
 * section/block events. Text width is measured with a Range so short
 * headings are enlarged as well as long ones shrunk. */
(() => {
  const SELECTOR = '.hero-picture__heading--fit';
  const MIN_PX = 18;
  const MAX_PX = 240;
  const PROBE_PX = 100;

  const textWidth = (el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width;
  };

  const fit = (el) => {
    const box = el.parentElement;
    if (!box) return;
    const style = getComputedStyle(box);
    const available = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    if (available <= 0) return;
    el.style.fontSize = `${PROBE_PX}px`;
    const width = textWidth(el);
    if (!width) return;
    // 2px safety margin: letter-spacing and subpixel rounding otherwise
    // leave the last glyph a hair outside the box on small screens.
    const size = Math.floor((PROBE_PX * (available - 2)) / width);
    el.style.fontSize = `${Math.max(MIN_PX, Math.min(MAX_PX, size))}px`;
  };

  const fitAll = (root = document) => root.querySelectorAll(SELECTOR).forEach(fit);

  const observer =
    'ResizeObserver' in window
      ? new ResizeObserver((entries) => entries.forEach((entry) => fitAll(entry.target)))
      : null;

  const init = (root = document) => {
    fitAll(root);
    if (observer) root.querySelectorAll('.hero-picture .banner__box--fit').forEach((box) => observer.observe(box));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  if (document.fonts) document.fonts.ready.then(() => fitAll());
  window.addEventListener('resize', () => fitAll());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => fitAll(event.target.closest('.shopify-section') || document));
})();

/* hero-picture: fit-to-width for the headline/claim AND the on-image
 * USP tiles' title/subline (2026-09-18, extended same day).
 *
 * The headline/claim/USP row are all sized/positioned to reproduce the
 * GERMAN copy exactly, 1:1, at real measured sizes (see the measurement
 * comments in section-hero-picture.css — the USP tiles in particular
 * intentionally have NO legibility floor beyond a technical 5.5px
 * clamp minimum, per the owner's explicit 1:1 requirement). Other
 * storefront languages can be meaningfully longer ("TAVO MOTOROLERIS -
 * TAVO DALYS" lt for the headline, "WSZYSTKO DO TWOJEGO SKUTERA" pl for
 * a USP title, ...) and would otherwise overflow their box — the
 * headline/claim would get cut off by the hero's own `overflow: clip`,
 * a USP title/subline would wrap or ellipsis, which the owner also
 * explicitly rejected for German ("one line each, nothing cut").
 *
 * Progressive enhancement, same shape as the "single line" heading fit
 * above: once fonts are ready and on resize/orientation change, measure
 * each element's actual RENDERED text width — via a Range (see
 * `textWidth` below for why, not the element's own
 * getBoundingClientRect) — against an available width, and write a
 * <= 1 multiplier to a CSS custom property that the font-size in
 * section-hero-picture.css already multiplies its tuned base size by.
 * Every measurement resets the property to 1 and re-measures from that
 * clean baseline first (rather than compounding an existing fit) —
 * this is exactly why German, which always measures inside the
 * available width at 1, renders pixel-identical to before this change:
 * it is never touched.
 *
 * Two different notions of "available width" are used, per target:
 *  - headline/claim: the hero's own width minus a gutter on each side
 *    (desktop 6%, mobile 3.5% — both wider than German's own measured
 *    footprint, so this only ever engages for genuinely longer text).
 *  - USP title/subline: the tile's own fixed-width text column
 *    (.hero-picture__usp-text, sized in CSS to the original artwork's
 *    own measured text-block span) — its rendered clientWidth. Title
 *    and subline are fit independently (a long title doesn't force the
 *    subline to shrink and vice versa). */
(() => {
  const RESIZE_DEBOUNCE_MS = 150;
  const MIN_FIT = 0.25;
  const DESKTOP_QUERY = '(min-width: 750px)';

  const desktopMql = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
  const isDesktop = () => (desktopMql ? desktopMql.matches : window.innerWidth >= 750);

  const heroGutterAvailable = (gutterDesktop, gutterMobile) => (el) => {
    const hero = el.closest('.hero-picture');
    const heroWidth = hero ? hero.getBoundingClientRect().width : 0;
    if (!heroWidth) return 0;
    const gutter = isDesktop() ? gutterDesktop : gutterMobile;
    return heroWidth * (1 - gutter * 2);
  };

  // The USP title/subline's own parent (.hero-picture__usp-text) is a
  // fixed-width column (see section-hero-picture.css) matching the
  // original artwork's own measured text-block span — its clientWidth
  // IS the target "must fit within this" width directly.
  const parentClientWidth = (el) => (el.parentElement ? el.parentElement.clientWidth : 0);

  const targets = [
    { selector: '.hero-picture__headline', cssVar: '--hero-headline-fit', available: heroGutterAvailable(0.06, 0.035) },
    { selector: '.hero-picture__claim', cssVar: '--hero-claim-fit', available: heroGutterAvailable(0.06, 0.035) },
    { selector: '.hero-picture__usp-title', cssVar: '--usp-title-fit', available: parentClientWidth },
    { selector: '.hero-picture__usp-subline', cssVar: '--usp-subline-fit', available: parentClientWidth },
  ];

  // `el.getBoundingClientRect()` measures the ELEMENT'S OWN BOX, which
  // for a block-level, width:auto node (the headline is an <h1>, the
  // claim a <p>, the USP title/subline are block spans in a fixed-width
  // column) is however wide its containing block is — NOT the actual
  // (nowrap, potentially overflowing) text content, so it stays
  // constant regardless of how long the text is and never actually
  // detects overflow. A Range selecting the text content, same
  // technique as the "single line" heading fit above, measures the
  // true painted text extent instead; it still reflects any ancestor
  // CSS transform (the headline's scaleX(), transforms affect all
  // descendant painting, Range rects included), so no separate
  // un-transform math is needed.
  const textWidth = (el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    return range.getBoundingClientRect().width;
  };

  const fitOne = (el, target) => {
    const available = target.available(el);
    if (available <= 0) return;

    // Reset to the clean baseline (1) and measure from there every
    // time, rather than adjusting an existing factor — this is what
    // lets the element grow back if e.g. the viewport widens or
    // rotates, and is why an already-fitting language (German) is
    // simply never written to.
    el.style.setProperty(target.cssVar, '1');
    const rendered = textWidth(el);
    if (!rendered || rendered <= available) return;

    // 0.5% extra margin: an exact available/rendered ratio can still
    // round back up to a fraction of a pixel over the box at the new
    // font-size (subpixel layout rounding, not measurement error) —
    // enough for text-overflow: ellipsis to trigger anyway despite the
    // fit having "worked". Same kind of safety margin as the "single
    // line" heading fit above.
    const needed = Math.max(MIN_FIT, (available / rendered) * 0.96);
    el.style.setProperty(target.cssVar, String(needed));
  };

  const fitAll = (root = document) => {
    targets.forEach((target) => {
      root.querySelectorAll(target.selector).forEach((el) => fitOne(el, target));
    });
  };

  let resizeTimer = null;
  const debouncedFitAll = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => fitAll(), RESIZE_DEBOUNCE_MS);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => fitAll());
  } else {
    fitAll();
  }
  if (document.fonts) document.fonts.ready.then(() => fitAll());
  window.addEventListener('resize', debouncedFitAll);
  window.addEventListener('orientationchange', debouncedFitAll);
  document.addEventListener('shopify:section:load', (event) => fitAll(event.target));
  document.addEventListener('shopify:block:select', (event) => fitAll(event.target.closest('.shopify-section') || document));
})();
